<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { picoSearch } from '@scmmishra/pico-search';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import PageLayout from 'dashboard/components-next/assistant/PageLayout.vue';
import AssistantCard from 'dashboard/components-next/assistant/shared/AssistantCard.vue';
import CreateAgentDialog from 'dashboard/components-next/assistant/pageComponents/agent/CreateAgentDialog.vue';
import DeleteDialog from 'dashboard/components-next/assistant/pageComponents/DeleteDialog.vue';

const store = useStore();

const uiFlags = useMapGetter('assistantAgents/getUIFlags');
const assistants = useMapGetter('assistantAgents/getRecords');
const assistantsMeta = useMapGetter('assistantAgents/getMeta');
const isFetching = computed(() => uiFlags.value.fetchingList);

// Поиск по загруженным записям (клиентская фильтрация — под масштаб SMB).
const searchQuery = ref('');
const filteredAssistants = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return assistants.value;
  return picoSearch(assistants.value, query, ['name', 'description']);
});

const dialogType = ref('');
const selectedAssistant = ref(null);
const createAssistantDialog = ref(null);
const deleteAssistantDialog = ref(null);
// Р16 п.2: см. agents/Index.vue — не обнуляем selectedAssistant при переходе
// «форма → подтверждение удаления», иначе DeleteDialog исчезает до открытия.
const pendingDelete = ref(false);

const handleCreate = () => {
  dialogType.value = 'create';
  selectedAssistant.value = null;
  nextTick(() => createAssistantDialog.value.dialogRef.open());
};

const handleEdit = () => {
  dialogType.value = 'edit';
  nextTick(() => createAssistantDialog.value.dialogRef.open());
};

const handleDelete = () => {
  nextTick(() => deleteAssistantDialog.value.dialogRef.open());
};

const handleAction = ({ action, id }) => {
  selectedAssistant.value = assistants.value.find(
    assistant => id === assistant.id
  );
  nextTick(() => {
    if (action === 'delete') handleDelete();
    else if (action === 'edit') handleEdit();
  });
};

const handleDeleteFromForm = () => {
  pendingDelete.value = true;
  createAssistantDialog.value?.dialogRef?.close();
  nextTick(() => {
    handleDelete();
    pendingDelete.value = false;
  });
};

const handleCreateClose = () => {
  dialogType.value = '';
  if (!pendingDelete.value) selectedAssistant.value = null;
};

const fetchAssistants = (page = 1) => {
  store.dispatch('assistantAgents/get', { page, kind: 'operator_assistant' });
};

const onPageChange = page => fetchAssistants(page);

onMounted(() => fetchAssistants());
</script>

<template>
  <PageLayout
    v-model:search-query="searchQuery"
    :header-title="$t('ASSISTANT.OPERATOR_ASSISTANTS.HEADER')"
    :description="$t('ASSISTANT.OPERATOR_ASSISTANTS.PAGE_DESCRIPTION')"
    :search-placeholder="$t('ASSISTANT.OPERATOR_ASSISTANTS.SEARCH_PLACEHOLDER')"
    :empty-message="$t('ASSISTANT.OPERATOR_ASSISTANTS.EMPTY_STATE.TITLE')"
    :button-label="$t('ASSISTANT.OPERATOR_ASSISTANTS.ADD_NEW')"
    :button-policy="['administrator']"
    :total-count="assistantsMeta.totalCount"
    :current-page="assistantsMeta.page"
    :is-fetching="isFetching"
    :is-empty="!assistants.length"
    :show-pagination-footer="!isFetching && !!assistants.length"
    :feature-flag="FEATURE_FLAGS.AI_ASSISTANT"
    @update:current-page="onPageChange"
    @click="handleCreate"
  >
    <template #body>
      <div
        v-if="!filteredAssistants.length"
        class="py-6 text-sm text-center text-n-slate-11"
      >
        {{ $t('ASSISTANT.OPERATOR_ASSISTANTS.SEARCH_EMPTY') }}
      </div>
      <div v-else class="flex flex-col gap-4">
        <AssistantCard
          v-for="assistant in filteredAssistants"
          :id="assistant.id"
          :key="assistant.id"
          :name="assistant.name"
          :description="assistant.description"
          :updated-at="assistant.updated_at"
          @action="handleAction"
        />
      </div>
    </template>

    <CreateAgentDialog
      v-if="dialogType"
      ref="createAssistantDialog"
      :type="dialogType"
      :selected-agent="selectedAssistant"
      kind="operator_assistant"
      @close="handleCreateClose"
      @delete="handleDeleteFromForm"
    />

    <DeleteDialog
      v-if="selectedAssistant"
      ref="deleteAssistantDialog"
      :entity="selectedAssistant"
      type="Agents"
      translation-key="OPERATOR_ASSISTANTS"
    />
  </PageLayout>
</template>
